import { useEffect, useRef, useState } from "react";
import { useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Phone, PhoneOff, Play, Pause, Square, MessageSquare } from "lucide-react";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { LeadDrafts } from "../leads/LeadShow";
import { useDialSession, type Disposition, type QueueLead } from "./useDialSession";
import { TabBar, StatTile, SectionCard, Pill, ScoreDot, formatPhone } from "../me-ui";

const GLOBAL = "__global__";

// Embedded Power Dialer screen (/dialer) — bespoke-page convention. All session
// logic lives in the dialer-core hook (useDialSession); this file is presentation
// only (re-skinned in me-ui, D64). Behavior is UNCHANGED: conference-first, FL
// consent, disposition→auto-advance, campaign-scoped + global mode.

// Semantic color token (dial_dispositions.color) → tailwind button classes.
const DISP_COLOR: Record<string, string> = {
  green: "bg-emerald-600 hover:bg-emerald-700 text-white",
  blue: "bg-sky-600 hover:bg-sky-700 text-white",
  indigo: "bg-indigo-600 hover:bg-indigo-700 text-white",
  amber: "bg-amber-500 hover:bg-amber-600 text-white",
  orange: "bg-orange-500 hover:bg-orange-600 text-white",
  red: "bg-red-600 hover:bg-red-700 text-white",
  slate: "bg-slate-500 hover:bg-slate-600 text-white",
  zinc: "bg-zinc-500 hover:bg-zinc-600 text-white",
};
const tempTone: Record<string, "red" | "amber" | "blue"> = { hot: "red", warm: "amber", cold: "blue" };
const ACTIVITY_LABEL: Record<string, string> = {
  call: "Call", sms: "SMS", email: "Email", rvm: "Voicemail",
  direct_mail: "Mail", note: "Note", task: "Task", status_change: "Status",
};
const dispTone = (d: string | null): "green" | "amber" | "red" | "grey" =>
  d === "connected" ? "green" : d === "no_answer" || d === "voicemail" ? "amber" : d === "wrong" || d === "dead" ? "red" : "grey";

function fmtTalk(s: number): string {
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

// --- inline activity thread for the current lead -----------------------------
const ActivityThread = ({ leadId, reloadKey }: { leadId: string; reloadKey?: number }) => {
  const [rows, setRows] = useState<any[]>([]);
  useEffect(() => {
    getSupabaseClient().from("activities").select("*").eq("lead_id", leadId)
      .order("occurred_at", { ascending: false }).limit(30)
      .then(({ data }) => setRows(data ?? []));
  }, [leadId, reloadKey]);
  if (!rows.length) return <p className="text-sm text-muted-foreground">No activity yet.</p>;
  return (
    <ul className="space-y-2 max-h-64 overflow-auto">
      {rows.map((a) => (
        <li key={a.id} className="flex gap-2 text-sm border-b border-border/60 pb-2">
          <Pill tone="grey">{ACTIVITY_LABEL[a.type] ?? a.type}</Pill>
          <div className="flex-1">
            <div>{a.body || a.disposition || "—"}</div>
            <div className="text-xs text-muted-foreground">
              {a.direction !== "none" ? `${a.direction} · ` : ""}{new Date(a.occurred_at).toLocaleString()}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
};

// --- D34 script picker (renders against the live lead) -----------------------
const ScriptPanel = ({ leadId }: { leadId: string }) => {
  const [scripts, setScripts] = useState<any[]>([]);
  const [selected, setSelected] = useState("");
  const [rendered, setRendered] = useState<string | null>(null);
  useEffect(() => {
    getSupabaseClient().from("scripts").select("id,name,body,script_type,lead_type")
      .eq("is_active", true).order("sort_order").order("name")
      .then(({ data }) => setScripts(data ?? []));
  }, []);
  useEffect(() => {
    if (!selected) { setRendered(null); return; }
    const s = scripts.find((x) => x.id === selected);
    if (!s) return;
    getSupabaseClient().rpc("render_template", { p_body: s.body, p_lead_id: leadId })
      .then(({ data }) => setRendered((data as string) ?? s.body));
  }, [selected, leadId, scripts]);
  return (
    <SectionCard title="Script">
      {scripts.length === 0 ? (
        <div className="text-xs text-muted-foreground">No scripts yet — add them in Settings → Scripts.</div>
      ) : (
        <Select value={selected || "__none__"} onValueChange={(v) => setSelected(v === "__none__" ? "" : v)}>
          <SelectTrigger className="h-9"><SelectValue placeholder="Pick a script…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Pick a script…</SelectItem>
            {scripts.map((s) => <SelectItem key={s.id} value={s.id}>{s.script_type ? `[${s.script_type}] ` : ""}{s.name}{s.lead_type ? ` · ${s.lead_type}` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {rendered != null && (
        <div className="rounded-md border bg-muted/40 p-2 text-sm whitespace-pre-wrap max-h-72 overflow-auto">{rendered}</div>
      )}
    </SectionCard>
  );
};

// --- in-session SMS composer (manual, agent-initiated, 1:1) -------------------
const SmsComposer = ({
  leadId, toNumber, windowOpen, smsEnabled, onSent,
}: {
  leadId: string; toNumber: string | null; windowOpen: boolean; smsEnabled: boolean; onSent: () => void;
}) => {
  const notify = useNotify();
  const [templates, setTemplates] = useState<any[]>([]);
  const [selected, setSelected] = useState("");
  const [text, setText] = useState("");
  const [contactId, setContactId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    getSupabaseClient().from("templates").select("id,key,name,body").eq("channel", "sms").order("name")
      .then(({ data }) => setTemplates(data ?? []));
  }, []);
  useEffect(() => {
    setText(""); setSelected("");
    getSupabaseClient().from("leads").select("primary_contact_id").eq("id", leadId).maybeSingle()
      .then(({ data }) => setContactId((data as { primary_contact_id?: string } | null)?.primary_contact_id ?? null));
  }, [leadId]);

  const pick = async (key: string) => {
    setSelected(key);
    const t = templates.find((x) => x.key === key);
    if (!t) { setText(""); return; }
    const { data } = await getSupabaseClient().rpc("render_template", { p_body: t.body, p_lead_id: leadId });
    setText((data as string) ?? t.body);
  };

  const send = async () => {
    if (!toNumber || !text.trim()) return;
    setSending(true);
    const sb = getSupabaseClient();
    const { data: draft, error: dErr } = await sb.from("drafts").insert({
      lead_id: leadId, contact_id: contactId, channel: "sms",
      to_address: toNumber, body: text.trim(), status: "pending", created_by: "manual",
      template_key: selected || null,
    }).select("id").single();
    if (dErr || !draft) { setSending(false); notify(`Could not draft: ${dErr?.message ?? "unknown"}`, { type: "error" }); return; }
    const { data: sess } = await sb.auth.getSession();
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-draft`, {
      method: "POST",
      headers: {
        apikey: import.meta.env.VITE_SB_PUBLISHABLE_KEY as string,
        Authorization: `Bearer ${sess.session?.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ draft_id: (draft as { id: string }).id }),
    });
    const d = await res.json().catch(() => ({}));
    setSending(false);
    if (!res.ok) { notify(d?.error ? `Not sent: ${d.error}` : `Send failed (${res.status})`, { type: "error" }); return; }
    notify("Text sent", { type: "success" });
    setText(""); setSelected("");
    onSent();
  };

  const disabledReason =
    !smsEnabled ? "10DLC pending — business SMS is disabled until A2P registration is live."
    : !windowOpen ? "Outside the send window (8am–9pm local)."
    : !toNumber ? "No number on file for this lead."
    : null;

  return (
    <SectionCard title="Send a text" right={toNumber ? <span className="text-xs text-muted-foreground font-mono">{formatPhone(toNumber)}</span> : null}>
      {templates.length > 0 && (
        <Select value={selected || "__none__"} onValueChange={(v) => pick(v === "__none__" ? "" : v)} disabled={!!disabledReason}>
          <SelectTrigger className="h-9"><SelectValue placeholder="Template… (or type below)" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Template… (or type below)</SelectItem>
            {templates.map((t) => <SelectItem key={t.id} value={t.key}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" disabled={!!disabledReason || sending} data-dialer-input="1" />
      <div className="flex items-center gap-2">
        <Button disabled={!!disabledReason || sending || !text.trim()} onClick={send}>
          <MessageSquare className="mr-2 h-4 w-4" /> {sending ? "Sending…" : "Send text"}
        </Button>
      </div>
      {disabledReason && <div className="text-xs text-amber-700 dark:text-amber-400">{disabledReason}</div>}
    </SectionCard>
  );
};

// --- disposition bar ---------------------------------------------------------
const CALLBACK_CODES = new Set(["callback"]);
function isTyping(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

const DispositionBar = ({
  dispositions, disabled, onPick,
}: { dispositions: Disposition[]; disabled: boolean; onPick: (code: string, opts?: { callbackAt?: string }) => void }) => {
  const [callbackFor, setCallbackFor] = useState<string | null>(null);
  const [when, setWhen] = useState("");
  const pick = (code: string) => (CALLBACK_CODES.has(code) ? setCallbackFor(code) : onPick(code));
  const pickRef = useRef(pick);
  pickRef.current = pick;
  useEffect(() => {
    if (disabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (isTyping() || e.metaKey || e.ctrlKey || e.altKey) return;
      const n = parseInt(e.key, 10);
      if (!Number.isNaN(n) && n >= 1 && n <= dispositions.length) { e.preventDefault(); pickRef.current(dispositions[n - 1].code); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [disabled, dispositions]);

  return (
    <SectionCard title="Disposition" sub={`Press 1–${dispositions.length} to disposition.`}>
      <div className="flex flex-wrap gap-2">
        {dispositions.map((d, i) => (
          <Button key={d.id} disabled={disabled}
            className={`rounded-full ${DISP_COLOR[d.color ?? ""] ?? "bg-slate-600 hover:bg-slate-700 text-white"}`}
            onClick={() => pick(d.code)}>
            <span className="mr-1.5 opacity-70 text-xs tabular-nums">{i + 1}</span>{d.label}
          </Button>
        ))}
      </div>
      {callbackFor && (
        <div className="flex items-center gap-2 pt-1">
          <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="max-w-xs" />
          <Button size="sm" disabled={!when} onClick={() => { onPick("callback", { callbackAt: new Date(when).toISOString() }); setCallbackFor(null); setWhen(""); }}>Set callback</Button>
          <Button size="sm" variant="ghost" onClick={() => { setCallbackFor(null); setWhen(""); }}>Cancel</Button>
        </div>
      )}
    </SectionCard>
  );
};

// --- lead card ---------------------------------------------------------------
const LeadCard = ({ lead }: { lead: QueueLead }) => (
  <SectionCard
    title={<span className="text-base">{lead.contact_name || "(no name)"}</span>}
    right={<span className="flex items-center gap-2">
      <Pill tone="blue" dot>{String(lead.status).replace("_", " ")}</Pill>
      {lead.temperature && <Pill tone={tempTone[lead.temperature] ?? "grey"}>{lead.temperature}</Pill>}
    </span>}>
    <div className="text-sm"><span className="text-muted-foreground">Property: </span>
      {[lead.address_line1, lead.city, lead.county].filter(Boolean).join(", ") || "—"}</div>
    <div className="text-sm flex items-center gap-1.5"><span className="text-muted-foreground">Best number: </span>
      <ScoreDot score={lead.best_activity_score} /><span className="font-mono font-semibold">{formatPhone(lead.best_number)}</span>
      <span className="text-muted-foreground">· {lead.workable_numbers} workable · score {lead.best_activity_score ?? "—"}</span></div>
    <div className="text-sm"><span className="text-muted-foreground">Attempts: </span>{lead.call_attempts ?? 0} calls</div>
  </SectionCard>
);

const DialScreen = ({ startCampaignId }: { startCampaignId?: string | null }) => {
  const s = useDialSession();
  const inCall = s.callState === "connecting" || s.callState === "ringing" || s.callState === "open";
  const active = s.status === "active";
  const [threadReload, setThreadReload] = useState(0);
  const [pickedCampaign, setPickedCampaign] = useState<string>(startCampaignId ?? GLOBAL);
  const [campaigns, setCampaigns] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    getSupabaseClient().from("campaigns").select("id,name").eq("audience", "raw_lead").eq("status", "active").order("name")
      .then(({ data }) => setCampaigns((data as any[]) ?? []));
  }, []);
  useEffect(() => { if (startCampaignId) setPickedCampaign(startCampaignId); }, [startCampaignId]);

  const [smsEnabled, setSmsEnabled] = useState(true);
  useEffect(() => {
    getSupabaseClient().from("config").select("value").eq("key", "sms_10dlc_ready").maybeSingle()
      .then(({ data }) => { if ((data as { value?: unknown } | null)?.value === false) setSmsEnabled(false); });
  }, []);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Single-line preview &amp; power dialing over a campaign's raw leads (or all raw leads). Every dial is
        agent-initiated; calls record with a Florida consent notice and file to the lead automatically.
      </p>

      {/* Session bar */}
      <div className="rounded-lg border bg-card p-3 flex flex-wrap items-center gap-3">
        <Select value={pickedCampaign} onValueChange={setPickedCampaign} disabled={active || s.status === "paused"}>
          <SelectTrigger className="h-9 w-64"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={GLOBAL}>All raw leads (global)</SelectItem>
            {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="inline-flex rounded-md border overflow-hidden">
          {(["preview", "power"] as const).map((m) => (
            <button key={m}
              className={`px-3 py-1.5 text-sm font-medium capitalize ${s.mode === m ? "bg-primary text-primary-foreground" : "bg-background"}`}
              onClick={() => s.setMode(m)} disabled={active} title={active ? "Stop the session to change mode" : ""}>{m}</button>
          ))}
        </div>
        {s.status === "idle" || s.status === "ended" ? (
          <Button disabled={!s.agentReady || s.busy} onClick={() => s.start(pickedCampaign === GLOBAL ? null : pickedCampaign)}>
            <Play className="mr-2 h-4 w-4" /> Start session
          </Button>
        ) : (
          <>
            {active ? <Button variant="outline" onClick={s.pause}><Pause className="mr-2 h-4 w-4" /> Pause</Button>
              : <Button variant="outline" onClick={s.resume}><Play className="mr-2 h-4 w-4" /> Resume</Button>}
            <Button variant="outline" onClick={s.end}><Square className="mr-2 h-4 w-4" /> End</Button>
          </>
        )}
        <div className="ml-auto flex items-center gap-4 text-sm">
          <span title="Dials this session"><b>{s.stats.dials}</b> dials</span>
          <span title="Connected calls"><b>{s.stats.connects}</b> connects</span>
          <span title="Dispositions"><b>{s.stats.dispositions}</b> dispositioned</span>
          <span title="Talk time"><b>{fmtTalk(s.stats.talk_time_s)}</b> talk</span>
          {s.remaining != null && <Pill tone="grey">{s.remaining} in queue</Pill>}
        </div>
      </div>

      {!s.windowOpen && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-300 text-sm px-3 py-2">
          Outside the calling window (8am–9pm local) — dialing is disabled.
        </div>
      )}
      {s.error && <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 text-red-900 dark:text-red-300 text-sm px-3 py-2">{s.error}</div>}

      {s.status === "idle" ? (
        <div className="text-sm text-muted-foreground">Pick a mode and press <b>Start session</b> to begin working the queue.</div>
      ) : !s.currentLead ? (
        <div className="text-sm text-muted-foreground">{active ? "Queue is empty — no eligible leads right now." : "Session paused."}</div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-4">
            <LeadCard lead={s.currentLead} />
            <div className="rounded-lg border bg-card p-3 flex items-center gap-3">
              {!inCall ? (
                <Button size="lg" disabled={!active || !s.currentLead.best_number || !s.windowOpen || s.busy} onClick={s.dial}>
                  <Phone className="mr-2 h-4 w-4" />{s.mode === "power" ? "Dial (power auto-advances)" : "Dial"}
                </Button>
              ) : (
                <Button size="lg" variant="destructive" onClick={s.hangUp}>
                  <PhoneOff className="mr-2 h-4 w-4" />{s.callState === "open" ? "Hang up" : `${s.callState}…`}
                </Button>
              )}
              <span className="text-sm text-muted-foreground capitalize">{s.callState}</span>
            </div>
            <SectionCard title="Activity"><ActivityThread leadId={s.currentLead.lead_id} reloadKey={threadReload} /></SectionCard>
          </div>
          <div className="space-y-4">
            <DispositionBar dispositions={s.dispositions} disabled={s.busy || !active} onPick={s.disposition} />
            <SmsComposer leadId={s.currentLead.lead_id} toNumber={s.currentLead.best_number} windowOpen={s.windowOpen} smsEnabled={smsEnabled} onSent={() => setThreadReload((n) => n + 1)} />
            <LeadDrafts leadId={s.currentLead.lead_id} />
            <ScriptPanel leadId={s.currentLead.lead_id} />
          </div>
        </div>
      )}
    </div>
  );
};

// --- Campaigns tab ------------------------------------------------------------
const CampaignsTab = ({ onDial }: { onDial: (id: string) => void }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const sb = getSupabaseClient();
    (async () => {
      const [{ data: camps }, { data: rl }] = await Promise.all([
        sb.from("campaigns").select("id,name,status,dialing_mode").eq("audience", "raw_lead").order("name"),
        sb.from("raw_leads").select("campaign_id,record_status").not("campaign_id", "is", null),
      ]);
      const total: Record<string, number> = {}; const converted: Record<string, number> = {};
      for (const r of (rl as any[]) ?? []) {
        total[r.campaign_id] = (total[r.campaign_id] ?? 0) + 1;
        if (r.record_status === "promoted") converted[r.campaign_id] = (converted[r.campaign_id] ?? 0) + 1;
      }
      setRows(((camps as any[]) ?? []).map((c) => ({ ...c, total: total[c.id] ?? 0, converted: converted[c.id] ?? 0 })));
      setLoaded(true);
    })();
  }, []);
  if (loaded && rows.length === 0) return (
    <div className="rounded-lg border border-dashed bg-muted/30 p-8 text-center text-sm text-muted-foreground">
      No campaigns yet — dialing <b>all raw leads in global mode</b>. Build a campaign in <b>Campaigns</b> to scope dialing to a specific set.
    </div>
  );
  return (
    <div className="rounded-lg border overflow-x-auto bg-card">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Campaign</TableHead><TableHead>Status</TableHead><TableHead>Mode</TableHead>
          <TableHead>Total Records</TableHead><TableHead>Lead Converted</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((c) => (
            <TableRow key={c.id}>
              <TableCell className="font-semibold">{c.name}</TableCell>
              <TableCell><Pill tone={c.status === "active" ? "green" : c.status === "paused" ? "amber" : "grey"} dot>{c.status}</Pill></TableCell>
              <TableCell><Pill tone="grey">{c.dialing_mode}</Pill></TableCell>
              <TableCell className="tabular-nums">{c.total}</TableCell><TableCell className="tabular-nums">{c.converted}</TableCell>
              <TableCell><Button size="sm" variant="secondary" onClick={() => onDial(c.id)} disabled={c.status !== "active"}>Dial</Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// --- Call Logs tab ------------------------------------------------------------
const CallLogsTab = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [disp, setDisp] = useState("__all__");
  useEffect(() => {
    getSupabaseClient().from("activities")
      .select("id,occurred_at,direction,duration_sec,disposition,recording_url,lead_id,user_id,users(full_name)")
      .eq("type", "call").order("occurred_at", { ascending: false }).limit(200)
      .then(({ data }) => setRows((data as any[]) ?? []));
  }, []);
  const dispositions = [...new Set(rows.map((r) => r.disposition).filter(Boolean))];
  const shown = disp === "__all__" ? rows : rows.filter((r) => r.disposition === disp);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Select value={disp} onValueChange={setDisp}>
          <SelectTrigger className="h-9 w-48"><SelectValue placeholder="All dispositions" /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">All dispositions</SelectItem>{dispositions.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="rounded-lg border overflow-x-auto bg-card">
        <Table>
          <TableHeader><TableRow>
            <TableHead>When</TableHead><TableHead>Agent</TableHead><TableHead>Direction</TableHead>
            <TableHead>Disposition</TableHead><TableHead>Duration</TableHead><TableHead>Recording</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shown.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No calls yet.</TableCell></TableRow> :
              shown.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="text-xs text-muted-foreground">{new Date(a.occurred_at).toLocaleString()}</TableCell>
                  <TableCell className="text-sm">{(Array.isArray(a.users) ? a.users[0]?.full_name : a.users?.full_name) ?? "—"}</TableCell>
                  <TableCell className="capitalize">{a.direction}</TableCell>
                  <TableCell>{a.disposition ? <Pill tone={dispTone(a.disposition)}>{a.disposition}</Pill> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                  <TableCell className="tabular-nums">{a.duration_sec != null ? fmtTalk(a.duration_sec) : "—"}</TableCell>
                  <TableCell>{a.recording_url ? <a className="text-blue-700 dark:text-blue-400 hover:underline" href={a.recording_url} target="_blank" rel="noreferrer">play</a> : "—"}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// --- Scheduled Callbacks ------------------------------------------------------
const CallbacksTab = () => {
  const [rows, setRows] = useState<any[]>([]);
  useEffect(() => {
    getSupabaseClient().from("me_campaign_call_queue")
      .select("lead_id,contact_name,best_number,next_action_at,campaign_id")
      .not("next_action_at", "is", null).order("next_action_at", { ascending: true }).limit(100)
      .then(({ data }) => setRows((data as any[]) ?? []));
  }, []);
  return (
    <div className="rounded-lg border overflow-x-auto bg-card">
      <Table>
        <TableHeader><TableRow><TableHead>Due</TableHead><TableHead>Contact</TableHead><TableHead>Number</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 ? <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">No scheduled callbacks.</TableCell></TableRow> :
            rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell className="text-xs">{r.next_action_at ? new Date(r.next_action_at).toLocaleString() : "—"}</TableCell>
                <TableCell>{r.contact_name || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{formatPhone(r.best_number)}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </div>
  );
};

// --- Stats tab: reuse kpi_dialer_day -----------------------------------------
const StatsTab = () => {
  const [kpi, setKpi] = useState<any>(null);
  useEffect(() => { getSupabaseClient().rpc("kpi_dialer_day").then(({ data }) => setKpi(data)); }, []);
  if (!kpi) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const pct = (v: any) => (v == null ? "—" : `${Math.round(Number(v) * 100)}%`);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Dials (today)" value={kpi.dials ?? 0} />
        <StatTile label="Connects" value={kpi.connects ?? 0} />
        <StatTile label="Connect rate" value={pct(kpi.connect_rate)} />
        <StatTile label="Talk time" value={kpi.talk_time_s != null ? fmtTalk(kpi.talk_time_s) : "—"} />
        <StatTile label="Dispositions" value={kpi.dispositions ?? 0} />
        <StatTile label="Sessions" value={kpi.sessions ?? 0} />
        <StatTile label="Agents active" value={kpi.agents ?? 0} />
      </div>
      <p className="text-xs text-muted-foreground">Today's team totals from the dialer session stats. Deeper metrics (calls/hr, contacts/hr, moved-to-leads, abandoned) build out as more call data accrues.</p>
    </div>
  );
};

// --- The Dialer module shell (me-ui TabBar over the reused WS-6 core, D54) ----
const DialerPage = () => {
  const [tab, setTab] = useState("dial");
  const [dialCampaign, setDialCampaign] = useState<string | null>(null);
  const startOnCampaign = (id: string) => { setDialCampaign(id); setTab("dial"); };
  return (
    <div className="p-2 max-w-6xl">
      <h1 className="text-[22px] font-extrabold tracking-tight flex items-center gap-2"><Phone className="h-5 w-5 text-blue-600" /> Dialer</h1>
      <p className="text-sm text-muted-foreground mb-3">Campaign-scoped power dialer — or global over all raw leads.</p>
      <TabBar accent="blue" className="mb-4" value={tab} onChange={setTab} tabs={[
        { value: "dial", label: "Dial" },
        { value: "campaigns", label: "Campaigns" },
        { value: "logs", label: "Call Logs" },
        { value: "callbacks", label: "Scheduled Callbacks" },
        { value: "stats", label: "Stats" },
      ]} />
      {tab === "dial" && <DialScreen startCampaignId={dialCampaign} />}
      {tab === "campaigns" && <CampaignsTab onDial={startOnCampaign} />}
      {tab === "logs" && <CallLogsTab />}
      {tab === "callbacks" && <CallbacksTab />}
      {tab === "stats" && <StatsTab />}
      <p className="text-xs text-muted-foreground mt-3">Inbox (inbound/returned calls) reuses WS-3 routing — see Voicemails; a dedicated Inbox tab is deferred.</p>
    </div>
  );
};

DialerPage.path = "/dialer";
export default DialerPage;
